import React, { useEffect, useRef, useState } from 'react'
import { Dialog } from 'components/Dialog'
import { hasWriteInstallationPermission } from 'lib/Permission'
import {
  getOverrideSnapshot,
  listOverrideHistory,
  OverrideHistoryEntry,
  OverrideRecoveryPreview,
  OverrideSnapshot,
  previewOverrideRecovery,
  recoverOverrides,
} from 'services/OverrideHistoryService'

const errorMessage = (error: unknown) => {
  const failure = error as { body?: { detail?: string }; message?: string }
  return failure?.body?.detail || failure?.message || 'Request failed. Try again.'
}

const date = (value: string) => new Date(value).toLocaleString()

type OverrideHistoryProps = {
  installationId: string
  onRecovered: () => void
  currentCommonId?: string | null
  currentCommonName?: string | null
}

export const OverrideHistory = ({
  installationId,
  onRecovered,
  currentCommonId,
  currentCommonName,
}: OverrideHistoryProps) => {
  const [open, setOpen] = useState(false)
  return (
    <>
      <button className="btn btn-primary-dark py-sm" aria-haspopup="dialog" onClick={() => setOpen(true)}>
        Override history ...
      </button>
      {open && (
        <Dialog modal ariaLabel="Override history" onClose={() => setOpen(false)} contentClassName="w-full">
          <HistoryContent
            key={installationId}
            installationId={installationId}
            onRecovered={onRecovered}
            currentCommonId={currentCommonId}
            currentCommonName={currentCommonName}
          />
        </Dialog>
      )}
    </>
  )
}

const HistoryContent = ({ installationId, onRecovered, currentCommonId, currentCommonName }: OverrideHistoryProps) => {
  const [entries, setEntries] = useState<OverrideHistoryEntry[]>([])
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [filters, setFilters] = useState(new URLSearchParams())
  const [loading, setLoading] = useState(true)
  const [snapshot, setSnapshot] = useState<OverrideSnapshot | null>(null)
  const [preview, setPreview] = useState<OverrideRecoveryPreview | null>(null)
  const [selecting, setSelecting] = useState(false)
  const [recovering, setRecovering] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const [confirmed, setConfirmed] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  const selection = useRef<AbortController | null>(null)
  const mounted = useRef(true)
  const content = useRef<HTMLDivElement>(null)
  const canWrite = hasWriteInstallationPermission()

  useEffect(() => {
    const controller = new AbortController()
    setLoading(true)
    setError('')
    listOverrideHistory(installationId, filters, controller.signal)
      .then((page) => {
        if (controller.signal.aborted) return
        setEntries((previous) => (filters.has('beforeId') ? [...previous, ...page] : page))
        setHasMore(page.length === 100)
      })
      .catch((failure) => {
        if (!controller.signal.aborted) setError(errorMessage(failure))
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false)
      })
    return () => controller.abort()
  }, [installationId, filters])

  useEffect(() => {
    const previousFocus = document.activeElement
    content.current?.focus()
    mounted.current = true
    return () => {
      mounted.current = false
      selection.current?.abort()
      if (previousFocus instanceof HTMLElement) previousFocus.focus()
    }
  }, [])

  const select = async (id: number) => {
    selection.current?.abort()
    const controller = new AbortController()
    selection.current = controller
    setSelecting(true)
    setSnapshot(null)
    setPreview(null)
    setConfirmed(false)
    setError('')
    setSuccess(false)
    try {
      if (canWrite) {
        const result = await previewOverrideRecovery(installationId, id, controller.signal)
        if (controller.signal.aborted) return
        setPreview(result)
        setSnapshot(result.snapshot)
      } else {
        const result = await getOverrideSnapshot(installationId, id, controller.signal)
        if (!controller.signal.aborted) setSnapshot(result)
      }
    } catch (failure) {
      if (!controller.signal.aborted) setError(errorMessage(failure))
    } finally {
      if (!controller.signal.aborted) setSelecting(false)
    }
  }

  const recover = async () => {
    if (!preview?.recoverable || !confirmed || recovering) return
    setRecovering(true)
    setError('')
    try {
      await recoverOverrides(installationId, preview.snapshot.entry.id, preview.contextVersion)
      if (!mounted.current) return
      setSuccess(true)
      setPreview(null)
      setSnapshot(null)
      const next = new URLSearchParams(filters)
      next.delete('beforeId')
      setFilters(next)
      onRecovered()
    } catch (failure) {
      if (!mounted.current) return
      setError(errorMessage(failure))
      setPreview(null)
      setConfirmed(false)
    } finally {
      if (mounted.current) setRecovering(false)
    }
  }

  const groups = new Map<string, OverrideHistoryEntry[]>()
  for (const entry of entries) {
    groups.set(entry.assortmentType, [...(groups.get(entry.assortmentType) ?? []), entry])
  }

  return (
    <div
      ref={content}
      tabIndex={-1}
      className="card p-lg overflow-auto"
      style={{ maxHeight: 'calc(100dvh - 2 * var(--spacing-lg))' }}
    >
      <h2 className="text-lg mb-md">Saved override sets</h2>
      <p>
        Each saved set contains one override type. Recovery requires no current overrides for that type and follows
        normal update scheduling. Other override types and the current common assignment are preserved.
      </p>
      <p>
        History shows retained saved sets. Dates below use your local time; ranges include sets effective during that
        period.
      </p>
      <form
        className="flex flex-wrap items-end gap-md my-md"
        onSubmit={(event) => {
          event.preventDefault()
          const next = new URLSearchParams()
          if (from) next.set('from', new Date(from).toISOString())
          if (to) next.set('to', new Date(to).toISOString())
          if (from && to && new Date(from) >= new Date(to)) {
            setError('Start must precede end.')
            return
          }
          selection.current?.abort()
          setSelecting(false)
          setSnapshot(null)
          setPreview(null)
          setEntries([])
          setFilters(next)
        }}
      >
        <label>
          From
          <input
            className="form-control"
            type="datetime-local"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            disabled={recovering}
          />
        </label>
        <label>
          Until (exclusive)
          <input
            className="form-control"
            type="datetime-local"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            disabled={recovering}
          />
        </label>
        <button className="btn btn-secondary" disabled={recovering}>
          Filter
        </button>
      </form>
      {error && (
        <p role="alert" className="alert alert-danger">
          {error}
        </p>
      )}
      {success && (
        <p role="status" className="alert alert-success">
          Overrides recovered. Current common unchanged.
        </p>
      )}
      {loading && <p role="status">Loading history...</p>}
      {!loading && entries.length === 0 && !error && <p>No historic sets match these filters.</p>}
      {Array.from(groups, ([id, sets]) => (
        <div key={id} className="my-md">
          <h3>{id}</h3>
          <ul>
            {sets.map((entry) => (
              <li key={entry.id} className="my-sm">
                <button className="btn btn-secondary" onClick={() => select(entry.id)} disabled={recovering}>
                  {date(entry.recordedAt)} — {entry.effectiveUntil ? date(entry.effectiveUntil) : 'current'}
                  {' · '}
                  {Object.entries(entry.counts)
                    .map(([type, count]) => `${type}: ${count}`)
                    .join(', ') || 'Empty set'}
                </button>
              </li>
            ))}
          </ul>
        </div>
      ))}
      {hasMore && (
        <button
          className="btn btn-secondary"
          disabled={loading || recovering}
          onClick={() => {
            const next = new URLSearchParams(filters)
            next.set('beforeId', String(entries[entries.length - 1].id))
            setFilters(next)
          }}
        >
          Load older sets
        </button>
      )}
      {selecting && <p role="status">Checking historic set...</p>}
      {snapshot && (
        <div className="my-md">
          <h3>
            Selected {snapshot.entry.assortmentType} set: {date(snapshot.entry.recordedAt)}
          </h3>
          {preview && (
            <p>
              Current common:{' '}
              {preview.currentCommonId === currentCommonId && currentCommonName
                ? currentCommonName
                : (preview.currentCommonId ?? 'None')}
            </p>
          )}
          <div className="overflow-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Reference</th>
                  <th>Action</th>
                  <th>Value</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(snapshot.modifications).flatMap(([type, overrides]) =>
                  overrides.map((override) => (
                    <tr key={`${type}-${override.referenceObjectId}-${override.actionType}`}>
                      <td>{type}</td>
                      <td>{override.referenceObjectId}</td>
                      <td>{override.actionType}</td>
                      <td>{override.actionValue}</td>
                    </tr>
                  )),
                )}
              </tbody>
            </table>
          </div>
          {preview?.issues.length ? (
            <div role="alert">
              <h4>Recovery blocked</h4>
              <ul>
                {preview.issues.map((issue) => (
                  <li key={issue}>{issue}</li>
                ))}
              </ul>
            </div>
          ) : null}
          {preview?.recoverable && (
            <label className="block my-md">
              <input
                type="checkbox"
                checked={confirmed}
                onChange={(event) => setConfirmed(event.target.checked)}
                disabled={recovering}
              />{' '}
              Recover {snapshot.entry.assortmentType} overrides for this installation without changing its common
              assignment.
            </label>
          )}
          {canWrite && (
            <button
              className="btn btn-primary-dark"
              disabled={!preview?.recoverable || !confirmed || recovering}
              onClick={recover}
            >
              {recovering ? 'Recovering...' : 'Recover overrides'}
            </button>
          )}
          {canWrite && !preview && (
            <button className="btn btn-secondary" onClick={() => select(snapshot.entry.id)}>
              Preview again
            </button>
          )}
        </div>
      )}
    </div>
  )
}
